// Best-effort detection of a country from a breakdown label so we can show a
// flag emoji next to it (matching the design). Recognises ISO 3166-1 alpha-2
// codes ("ES") and country display names in the active locale and English
// ("Spain", "España"). Returns null for anything that isn't clearly a country.

const CODES =
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW'.split(
    ' ',
  );

const CODE_SET = new Set(CODES);
const DIACRITICS = /[̀-ͯ]/g;

// Common alternate spellings / older or short names that Intl.DisplayNames no
// longer returns (e.g. "Turkey" is now "Türkiye", "Czechia" was "Czech
// Republic"). Keys are matched after normalize().
const ALIASES = {
  turkey: 'TR',
  'czech republic': 'CZ',
  uk: 'GB',
  'great britain': 'GB',
  britain: 'GB',
  england: 'GB',
  usa: 'US',
  'united states of america': 'US',
  america: 'US',
  korea: 'KR',
  russia: 'RU',
  'russian federation': 'RU',
  swaziland: 'SZ',
  burma: 'MM',
  macedonia: 'MK',
  'ivory coast': 'CI',
  'cape verde': 'CV',
  'east timor': 'TL',
  laos: 'LA',
  syria: 'SY',
  'viet nam': 'VN',
  uae: 'AE',
  holland: 'NL',
};

function normalize(value) {
  return String(value).toLowerCase().normalize('NFD').replace(DIACRITICS, '').trim();
}

function codeToFlag(code) {
  return String.fromCodePoint(...[...code].map(ch => 0x1f1e6 + ch.charCodeAt(0) - 65));
}

const nameMapCache = {};

// Builds a normalized "country name" -> code map for the given locale (plus
// English as a fallback), using Intl.DisplayNames so no name table is bundled.
function buildNameMap(locale) {
  if (nameMapCache[locale]) {
    return nameMapCache[locale];
  }
  const displays = ['en', locale]
    .filter((loc, i, arr) => loc && arr.indexOf(loc) === i)
    .map(loc => {
      try {
        return new Intl.DisplayNames([loc], { type: 'region' });
      } catch (_e) {
        return null;
      }
    })
    .filter(Boolean);

  const map = {};
  CODES.forEach(code => {
    displays.forEach(display => {
      const name = display.of(code);
      if (name && name !== code) {
        map[normalize(name)] = code;
        // Also index a variant without any parenthetical, e.g.
        // "Myanmar (Burma)" -> "myanmar".
        const bare = name.replace(/\s*\(.*?\)\s*/g, ' ').trim();
        if (bare && bare !== name) {
          map[normalize(bare)] = code;
        }
      }
    });
  });
  Object.entries(ALIASES).forEach(([name, code]) => {
    map[normalize(name)] = code;
  });
  nameMapCache[locale] = map;
  return map;
}

export default function getCountryFlag(label, locale = 'en') {
  if (!label) {
    return null;
  }
  const raw = String(label).trim();
  if (/^[a-z]{2}$/i.test(raw) && CODE_SET.has(raw.toUpperCase())) {
    return codeToFlag(raw.toUpperCase());
  }
  const code = buildNameMap(locale)[normalize(raw)];
  return code ? codeToFlag(code) : null;
}
